import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';

import { STORAGE_KEYS } from '../constants/storage-keys';
import { AccountStatus } from '../models/auth.enum';
import { AuthService } from '../services/auth.service';
import { ToastService } from '../services/toast.service';

/**
 * Gates every authenticated consignor-portal route.
 * JWT must be valid, `userOrigin` must be 'External', `roles` must include
 * 'consignor', and `accountStatus` must be Active (FR §2 / §4.3).
 * Unauthenticated requests park the target URL for post-login redirect.
 */
export const authGuard: CanActivateFn = (_route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const toast = inject(ToastService);

  if (!authService.isAuthenticated()) {
    sessionStorage.setItem(STORAGE_KEYS.RETURN_URL, state.url);
    router.navigate(['/login']);
    return false;
  }

  const userInfo = authService.getUserInfo();
  if (userInfo?.userOrigin !== 'External' || userInfo.externalUserType !== 'Consignor') {
    toast.error('Access Denied', 'You do not have permission to access the Consignor Portal.');
    router.navigate(['/login']);
    return false;
  }

  if (userInfo.accountStatus === AccountStatus.Disabled) {
    toast.error('Access Denied', 'Your account is disabled. Contact support.');
    authService.clearAuthData();
    router.navigate(['/login']);
    return false;
  }

  if (userInfo.accountStatus === AccountStatus.PendingVerification) {
    authService.clearAuthData();
    router.navigate(['/login'], { queryParams: { verify: '1' } });
    return false;
  }

  return true;
};
